import generatePicker from "antd/es/date-picker/generatePicker";
import { type PickerProps } from "antd/lib/date-picker/generatePicker";
import { endOfWeek, format, isEqual, isValid, startOfWeek } from "date-fns";
import dateFnsGenerateConfig from "rc-picker/lib/generate/dateFns";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";

import { cn } from "@btnet/utilities-core";

import { useDateFnsLocale } from "helpers/globalContext/dateFnsLocale";

import { useTracking } from "utilities/analytics/analytics";
import { tryParse } from "utilities/date/date";
import { addCloseOnScroll, getClosestModal, removeCloseOnScroll } from "utilities/helpers";
import { type KeyOfOrString } from "utilities/type/PropsOfType";

import { BTIconCalendarOutlined, BTIconCaretSmallDown } from "commonComponents/btWrappers/BTIcon";
import { DateDisplayDate } from "commonComponents/utilities/DateDisplay/DateDisplayDate";
import { ValueDisplay } from "commonComponents/utilities/ValueDisplay/ValueDisplay";

import "./BTDatePickerDate.less";

const DateDatePicker = generatePicker<Date>(dateFnsGenerateConfig);

interface IBTDatePickerDateProps<FormValues> {
    id: KeyOfOrString<FormValues>;

    value: Date | null | undefined;

    "data-testid": string;

    readOnly?: boolean;

    /**
     * Renders the date picker as a greyed-out, non-interactive control while keeping the input visible.
     * Distinct from `readOnly`, which swaps the control for a static value display.
     * @default false
     */
    disabled?: PickerProps<Date>["disabled"];

    /**
     *  This prop **only** effects what's displayed in readonly mode
     * @default false
     */
    showTime?: boolean;

    /**
     * With this flag the date picker is rendered as a dropdown - removing the clear icon, adding the chevron, and changing the date format
     * @default false
     */
    isDropDown?: boolean;

    /**
     * If true, the input will automatically focus when the component mounts
     * @default false
     */
    autoFocus?: boolean;

    /** pass formik setFieldValue */
    onChange: (field: KeyOfOrString<FormValues>, value: Date | undefined) => void;

    /** pass formik setFieldTouched
     * onBlur={() => setFieldTouched("fieldName", true)}
     */
    onBlur?: PickerProps<Date>["onBlur"];
    /**
     * Controls how the picker renders the dropdown values
     * @note time not supported, use BTTimePicker instead
     * @default "date"
     */
    picker?: Exclude<PickerProps<Date>["picker"], "time">;
    placeholder?: PickerProps<Date>["placeholder"];
    className?: PickerProps<Date>["className"];
    disabledDate?: PickerProps<Date>["disabledDate"];
    allowClear?: PickerProps<Date>["allowClear"];

    /**
     * Property that determines the form mode of the date picker. If "click-to-edit", then the date picker content is styled
     * like a similar "readonly" component, becoming editable on click; suitable for auto-save scenarios.
     * @default "always-editing"
     */
    formMode?: "always-editing" | "click-to-edit";

    /**
     * Placeholder to show when the date picker is focused. If not provided, uses the regular placeholder.
     */
    focusedPlaceholder?: string;

    /**
     * Function to determine the container for the dropdown.
     * @default getClosestModal
     */
    getPopupContainer?: (triggerNode: HTMLElement) => HTMLElement;
}

/**
 * This component is a wrapper around the antd DatePicker component and takes the builder's locale into account when parsing dates.
 * This component uses `Date` for the date's data type.
 */
export const BTDatePickerDate = memo(function BTDatePickerDateInternal<FormValues>(
    props: IBTDatePickerDateProps<FormValues>,
) {
    const {
        id,
        value,
        "data-testid": testid,
        onChange,
        onBlur,
        readOnly,
        disabled,
        picker,
        isDropDown = false,
        autoFocus = false,
        allowClear = true,
        formMode = "always-editing",
        focusedPlaceholder,
        getPopupContainer = getClosestModal,
        ...otherProps
    } = props;

    const [isOpen, setIsOpen] = useState(false);
    const [isFocused, setIsFocused] = useState(false);
    const tracking = useTracking({ element: "Date Picker", uniqueId: testid });
    const builderDateLocale = useDateFnsLocale();

    const fieldRef = useRef<HTMLDivElement>(null);
    // TODO: remove this when antd is upgraded to >=5.5.0 https://ant.design/changelog#550 -> DatePicker support changeOnBlur to trigger change event without clicking confirm.
    const currentStringValueForMouseOutIssue = useRef<string>();

    // Auto focus the input if autoFocus is true
    useEffect(() => {
        if (autoFocus && fieldRef.current) {
            const inputElement = fieldRef.current.querySelector("input");
            if (inputElement) {
                // Use a small timeout to ensure the component is fully rendered
                void setTimeout(() => {
                    inputElement.focus();
                }, 100);
            }
        }
    }, [autoFocus]);

    // Held in a ref so add/remove pass the *same* function object. removeEventListener matches on
    // reference, so a handler declared inside handleOpenChange (a fresh closure per call) could
    // never be removed — every open leaked another listener on the scroll container, and closing
    // removed none of them (ado-283026).
    const handleScrollRef = useRef<() => void>();

    const handleOpenChange = useCallback(
        (open: boolean) => {
            setIsOpen(open);

            // Track focus state for click-to-edit mode
            if (formMode === "click-to-edit") {
                setIsFocused(open);
            }

            // bind scroll event
            if (fieldRef.current) {
                if (open) {
                    // Replacing an existing handler would strand the previous one; drop it first.
                    if (handleScrollRef.current) {
                        removeCloseOnScroll(fieldRef.current, handleScrollRef.current);
                    }
                    handleScrollRef.current = () => handleOpenChange(false);
                    addCloseOnScroll(fieldRef.current, handleScrollRef.current);
                } else if (handleScrollRef.current) {
                    removeCloseOnScroll(fieldRef.current, handleScrollRef.current);
                    handleScrollRef.current = undefined;
                }
            }
        },
        [formMode],
    );

    useEffect(() => {
        return () => handleOpenChange(false);
    }, [handleOpenChange]);

    const currentInputElement = fieldRef.current?.querySelector("input");
    useEffect(() => {
        currentInputElement?.addEventListener("keyup", handleKeyUp);
        return () => {
            currentInputElement?.removeEventListener("keyup", handleKeyUp);
        };

        function handleKeyUp(e: KeyboardEvent) {
            const target = e.target as HTMLInputElement;
            currentStringValueForMouseOutIssue.current = target.value;
        }
    }, [currentInputElement]);

    const handleValueChange = useCallback(
        (date: Date | null) => {
            const dateNullOrUndefined = date ?? undefined;

            onChange(id, dateNullOrUndefined);
            tracking.trackEvent({
                event: "ValueChange",
                extraInfo: {
                    date: date?.toString(),
                    previousDate: value?.toString(),
                },
            });
            currentStringValueForMouseOutIssue.current = undefined;
        },
        [id, onChange, tracking, value],
    );

    const handleBlur = useCallback(
        (e: React.FocusEvent<HTMLInputElement>) => {
            if (onBlur) {
                onBlur(e);
            }
            const previousValue = value;
            if (currentStringValueForMouseOutIssue.current) {
                const parsedValue = tryParse(
                    currentStringValueForMouseOutIssue.current,
                    builderDateLocale.dateInputFormats ?? [],
                    new Date(),
                );
                if (
                    parsedValue == null ||
                    (isValid(parsedValue) &&
                        (!previousValue || !isEqual(parsedValue, previousValue)))
                ) {
                    handleValueChange(parsedValue);
                }
            }
        },
        [builderDateLocale.dateInputFormats, handleValueChange, onBlur, value],
    );

    const formatProp = useMemo<string[] | ((value: Date) => string)[] | undefined>(() => {
        return isDropDown
            ? getDateFormat(picker, builderDateLocale.dateInputFormats)
            : builderDateLocale.dateInputFormats;

        function getDateFormat(
            picker: IBTDatePickerDateProps<FormValues>["picker"],
            dateInputFormats: string[] | undefined = [],
        ) {
            if (picker === "week" || picker === "month") {
                const customMonthFormat = ["MMMM, yyyy", "MM/yyyy", "MMMM yyyy"];
                const weekFormat = "MMM dd";
                const customWeekStartEndFormat = (val: Date) =>
                    `${format(startOfWeek(val), weekFormat)} - ${format(
                        endOfWeek(val),
                        weekFormat + ", yyyy",
                    )}`;
                return picker === "week" ? [customWeekStartEndFormat] : customMonthFormat;
            }
            const customDayFormat = "EEE, MMM dd, y";
            return [customDayFormat, ...dateInputFormats];
        }
    }, [builderDateLocale.dateInputFormats, isDropDown, picker]);

    const isClickToEditViewMode = formMode === "click-to-edit" && !isFocused && !readOnly;

    // Default placeholder to "--" when in click-to-edit mode and no placeholder provided
    const basePlaceholder =
        formMode === "click-to-edit" && !otherProps.placeholder
            ? "--"
            : (otherProps.placeholder ?? "");

    // Use focusedPlaceholder when open or focused, otherwise use basePlaceholder
    const placeholder =
        (isOpen || isFocused) && focusedPlaceholder ? focusedPlaceholder : basePlaceholder;

    if (readOnly) {
        return (
            <ValueDisplay
                id={id as string}
                data-testid={testid}
                value={
                    value ? (
                        <DateDisplayDate
                            data-testid={`dataDisplay-${testid}`}
                            value={value}
                            showTime={otherProps.showTime}
                        />
                    ) : undefined
                }
                className={cn(otherProps.className, {
                    "padding-horizontal-xs": formMode === "click-to-edit",
                })}
            />
        );
    }

    // clearable is not compatible with dropdown mode because the clear button blocks the dropdown icon.
    const clearable = !isDropDown && allowClear;

    return (
        <div
            className={cn("BTDatePickerDate", {
                ClickToEditViewMode: isClickToEditViewMode,
            })}
            ref={fieldRef}
        >
            <DateDatePicker
                id={id as string}
                data-testid={testid}
                value={value}
                onChange={handleValueChange}
                onBlur={handleBlur}
                disabled={disabled}
                open={isOpen}
                onOpenChange={handleOpenChange}
                onFocus={() => handleOpenChange(true)}
                getPopupContainer={getPopupContainer}
                format={formatProp}
                defaultValue={undefined}
                picker={picker}
                placeholder={placeholder}
                allowClear={clearable}
                panelRender={handlePanelRender}
                className={cn(otherProps.className, {
                    isDropDown,
                })}
                suffixIcon={
                    isDropDown ? <BTIconCaretSmallDown size={20} /> : <BTIconCalendarOutlined />
                }
                disabledDate={otherProps.disabledDate}
            />
        </div>
    );
});

function handlePanelRender(originPanel: React.ReactNode) {
    return <div data-testid="datePanel">{originPanel}</div>;
}
